// In-drawer viewer: portaled into the drawer element (viewerHost) and
// absolutely positioned there, so it never escapes into a new tab or covers
// the page. Focus is trapped and restored; Escape is captured ahead of the
// drawer's own Escape handler.
import { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { T } from "../../../app/tokens.js";
import { safeMediaUrl } from "./MediaStrip.jsx";
import { mediaLabels } from "./mediaDisplay.js";
import { pageCountText, usePdfPageCount } from "./pdfPageCount.js";
import { Icon } from "./mediaIcons.jsx";

const FOCUSABLE = "button:not(:disabled),a[href],[tabindex]:not([tabindex='-1'])";

function ViewerImage({ item }) {
  const [broken, setBroken] = useState(false);
  const src = safeMediaUrl(item.url);
  if (!src || broken) return <div role="status" style={{ color: T.light, padding: 24, textAlign: "center" }}>Image unavailable. Try downloading the file.</div>;
  return <img src={src} alt={mediaLabels(item).primary} onError={() => setBroken(true)} style={{ display: "block", maxWidth: "100%", maxHeight: "100%", objectFit: "contain", margin: "auto" }} />;
}

function ViewerPdf({ item }) {
  const pages = usePdfPageCount(item, true);
  return <div data-testid="media-viewer-pdf" style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 10, color: T.white, padding: 32, textAlign: "center" }}>
    <Icon name="doc" size={56} />
    <div style={{ fontWeight: T.fwBold, fontSize: T.fsMd }}>PDF document</div>
    <div data-testid="media-viewer-pages" style={{ fontSize: T.fsSm, color: T.light }}>{pageCountText(pages)}</div>
    <div style={{ fontSize: T.fsSm, color: T.light, maxWidth: 320 }}>PDFs are not previewed here. Download the file to read it.</div>
  </div>;
}

export function MediaDrawerViewer({ items, activeId, onIndex, onClose, host }) {
  const panel = useRef(null);
  const closeRef = useRef(null);
  const restore = useRef(null);
  const titleId = useId();
  const index = items.findIndex(item => String(item.id) === String(activeId));
  const item = index >= 0 ? items[index] : null;
  const open = Boolean(item);
  const navRef = useRef({});
  navRef.current = { index, items, onIndex, onClose };

  useEffect(() => {
    if (!open) return undefined;
    restore.current = document.activeElement;
    closeRef.current?.focus();
    const key = event => {
      const { index: i, items: list, onIndex: go, onClose: close } = navRef.current;
      if (event.key === "Escape") { event.preventDefault(); event.stopPropagation(); event.stopImmediatePropagation?.(); close(); return; }
      if (!panel.current?.contains(document.activeElement)) return;
      if (event.key === "ArrowLeft" && i > 0) { event.preventDefault(); event.stopPropagation(); go(list[i - 1].id); }
      if (event.key === "ArrowRight" && i < list.length - 1) { event.preventDefault(); event.stopPropagation(); go(list[i + 1].id); }
      if (event.key === "Tab") {
        const nodes = [...panel.current.querySelectorAll(FOCUSABLE)];
        if (!nodes.length) return;
        const first = nodes[0], last = nodes[nodes.length - 1];
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
      }
    };
    // Window capture runs before any document/drawer listener.
    window.addEventListener("keydown", key, true);
    const focusIn = event => { if (panel.current && !panel.current.contains(event.target)) closeRef.current?.focus(); };
    document.addEventListener("focusin", focusIn);
    return () => {
      window.removeEventListener("keydown", key, true);
      document.removeEventListener("focusin", focusIn);
      const target = restore.current;
      restore.current = null;
      if (target && typeof target.focus === "function" && target.isConnected) setTimeout(() => target.focus(), 0);
    };
  }, [open]);

  if (!item) return null;
  const labels = mediaLabels(item);
  const download = safeMediaUrl(item.downloadUrl || item.url);
  const isImage = item.type === "image";
  const navBtn = { width: 42, height: 42, borderRadius: 21, border: `1px solid ${T.mid}`, background: T.dark, color: T.white, display: "inline-flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0 };
  const node = <div ref={panel} role="dialog" aria-modal="true" aria-labelledby={titleId} data-testid="media-drawer-viewer"
    style={{ position: host ? "absolute" : "fixed", inset: 0, zIndex: 50, background: T.dark, display: "flex", flexDirection: "column", padding: "14px 16px 18px", boxSizing: "border-box" }}>
    <div style={{ display: "flex", alignItems: "center", gap: 10, color: T.white }}>
      <h3 id={titleId} style={{ flex: 1, margin: 0, fontSize: T.fsMd, fontWeight: T.fwBold, overflowWrap: "anywhere" }}>{labels.primary}</h3>
      <button ref={closeRef} type="button" aria-label="Close viewer" data-testid="media-viewer-close" onClick={onClose} style={{ ...navBtn, width: 34, height: 34 }}><Icon name="close" /></button>
    </div>
    <div style={{ flex: 1, minHeight: 0, display: "flex", alignItems: "center", gap: 12, marginTop: 12 }}>
      {isImage && <button type="button" aria-label="Previous image" data-testid="media-viewer-prev" disabled={index === 0} onClick={() => onIndex(items[index - 1].id)} style={{ ...navBtn, opacity: index === 0 ? .35 : 1 }}><Icon name="left" /></button>}
      <div style={{ flex: 1, minWidth: 0, height: "100%", display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 10, overflow: "hidden", background: isImage ? T.dark : "transparent" }}>
        {isImage ? <ViewerImage key={item.id} item={item} /> : <ViewerPdf item={item} />}
      </div>
      {isImage && <button type="button" aria-label="Next image" data-testid="media-viewer-next" disabled={index === items.length - 1} onClick={() => onIndex(items[index + 1].id)} style={{ ...navBtn, opacity: index === items.length - 1 ? .35 : 1 }}><Icon name="right" /></button>}
    </div>
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, marginTop: 12, color: T.white, fontSize: T.fsSm }}>
      <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {labels.secondary && <span style={{ color: T.light }}>{labels.secondary}</span>}
        {isImage && <span aria-live="polite" data-testid="media-viewer-position"> {labels.secondary ? " · " : ""}{index + 1} of {items.length} {items.length === 1 ? "image" : "images"}</span>}
      </span>
      {download && <a href={download} download data-testid="media-viewer-download" style={{ color: T.white, fontWeight: T.fwBold, display: "inline-flex", gap: 6, alignItems: "center", textDecoration: "none" }}><Icon name="download" />Download</a>}
    </div>
  </div>;
  return createPortal(node, host || document.body);
}
